import { stockApi } from '@lib/axios';
import { apiCall } from './api-utils';

const tokenCache = new Map();

export function getTokenFromCache(symbol) {
  return tokenCache.get(symbol.split('#')[0]) ?? '';
}

export async function resolveToken(symbolInput) {
  const symbol = symbolInput.split('#')[0];
  if (tokenCache.has(symbol)) return tokenCache.get(symbol);

  const results = await apiCall(
    () => stockApi.get('/v5/search', { params: { query: symbol } }),
    'resolveToken',
  );
  if (!results) return '';

  const searchResults =
    results?.data?.values ||
    (Array.isArray(results?.data)
      ? results.data
      : Array.isArray(results)
        ? results
        : []);
  const token = String(searchResults.find((r) => r.symbol === symbol)?.token ?? '');
  tokenCache.set(symbol, token);
  return token;
}

export async function fetchInstrument(token) {
  const data = await apiCall(
    () => stockApi.get('/v2/instruments', { params: { tokens: token } }),
    'fetchInstrument',
  );
  return data?.data?.[0] ?? null;
}

export async function fetchBarsIntraday({ token, type, from, to, countBack }) {
  return apiCall(
    () =>
      stockApi.get('/v6/pattern', {
        params: { tokens: token, type, from, to, ...(countBack ? { countBack } : {}) },
      }),
    'fetchBarsIntraday',
  );
}

export async function fetchBarsDaily(token, from, to) {
  return apiCall(
    () => stockApi.get('/v4/eod', { params: { token, from, to, type: 'D' } }),
    'fetchBarsDaily',
  );
}

export async function fetchMarksData({ token, type, from, to }) {
  return apiCall(
    () => stockApi.get('/v6/pattern', { params: { tokens: token, type, from, to } }),
    'fetchMarksData',
  );
}

export async function fetchSectors() {
  return apiCall(() => stockApi.get('/v1/sectors'), 'fetchSectors');
}

export async function fetchTicks(tokens) {
  return apiCall(
    () => stockApi.get('/v1/ticks', { params: { tokens: tokens.join(',') } }),
    'fetchTicks',
  );
}

export async function searchSymbols(query) {
  return apiCall(
    () => stockApi.get('/v5/search', { params: { query: query } }),
    'searchSymbols',
  );
}

export async function getAllFutureInstruments(params) {
  return apiCall(
    () => stockApi.get('/v1/get_all_future_instruments', { params }),
    'getAllFutureInstruments',
  );
}

export async function getAllFutureTicks(params) {
  return apiCall(
    () => stockApi.get('/v1/get_all_future_ticks', { params }),
    'getAllFutureTicks',
  );
}

export async function getStockSectorAPI() {
  return apiCall(() => stockApi.get('/v1/get_index_sector'), 'getStockSectorAPI');
}

export async function getTicksData(tokens) {
  return apiCall(() => stockApi.get(`/v1/ticks?tokens=${tokens}`), 'getTicksData');
}

export async function getIndexAtmInstruments() {
  return apiCall(
    () => stockApi.get('/v1/get_index_atm_instruments'),
    'getIndexAtmInstruments',
  );
}

export async function getOptionChainInstrumentNames() {
  return apiCall(() => stockApi.get('/v1/search'), 'getOptionChainInstrumentNames');
}

export async function getOptionExpiries(instrument) {
  return apiCall(
    () => stockApi.get(`/v1/get_option_expiry?name=${encodeURIComponent(instrument)}`),
    'getOptionExpiries',
  );
}

export async function getSpotToken(instrument, exchange) {
  return apiCall(
    () =>
      stockApi.get(
        `/v1/instruments?name=${encodeURIComponent(instrument)}&exchange=${exchange}`,
      ),
    'getSpotToken',
  );
}

export async function getOptionChainInstrumentsData(instrument, expiry) {
  return apiCall(
    () =>
      stockApi.get(
        `/v1/get_option_chain_instruments?name=${encodeURIComponent(instrument)}&expiry=${expiry}`,
      ),
    'getOptionChainInstrumentsData',
  );
}

export async function getExchangeHolidays(exchange) {
  return apiCall(
    () => stockApi.get(`/v1/get_exchange_holidays?exchange=${exchange}`),
    'getExchangeHolidays',
  );
}

export async function getSpecialSession() {
  return apiCall(() => stockApi.get(`/v1/get_special_session`), 'getSpecialSession');
}

export async function getServerTimestamp() {
  return apiCall(() => stockApi.get('/v1/timestamp'), 'getServerTimestamp');
}


export async function fetchTicksbyTime(tokens, timestamp) {
  return apiCall(
    () =>
      stockApi.get('/v3/ticks', {
        params: {
          tokens: tokens.join(','),
          timestamp,
        },
      }),
    'fetchTicksbyTime',
  );
}

export async function fetchPatternData({ tokens, type, from, to }) {
  return apiCall(
    () => stockApi.get('/v7/pattern', { params: { tokens, type, from, to } }),
    'fetchPatternData',
  );
}

export async function fetchEodData({ tokens, from, to, type = 'D' }) {
  return apiCall(
    () => stockApi.get('/v6/get_eod', { params: { tokens, from, to, type } }),
    'fetchEodData',
  );
}

export async function fetchAggregatedOI({ token, type, from, to, expiry, strike }) {
  return apiCall(
    () =>
      stockApi.get('/v1/get_aggregated_oi', {
        params: { token, type, from, to, expiry, strike },
      }),
    'fetchAggregatedOI',
  );
}

export async function fetchSeasonalityData(token) {
  return apiCall(
    () => stockApi.get('/v2/seasonality', { params: { token } }),
    'fetchSeasonalityData',
  );
}

export async function fetchSpotCumulative({ filter, resolution, from, to }) {
  return apiCall(
    () =>
      stockApi.get('/v1/get_spot_cumulative', {
        params: { filter, type: resolution, from, to },
      }),
    'fetchSpotCumulative',
  );
}

export async function fetchFuturesEodAnalysis(date) {
  return apiCall(
    () => stockApi.get('/v4/rollover', { params: { date } }),
    'fetchFuturesEodAnalysis',
  );
}

export async function getPreMarketInstruments() {
  return apiCall(
    () => stockApi.get('/v1/get_pre_market_instruments'),
    'getPreMarketInstruments',
  );
}

export async function getPreMarketTicks() {
  return apiCall(
    () => stockApi.get('/v1/get_pre_market_ticks'),
    'getPreMarketTicks',
  );
}

export async function fetchCashMarket(to, from) {
  return apiCall(
    () => stockApi.get('/v1/get_cash_market', { params: { to, from } }),
    'fetchCashMarket',
  );
}

export async function fetchIndexStats(to, from) {
  return apiCall(
    () => stockApi.get('/v1/get_index_stats', { params: { to, from } }),
    'fetchIndexStats',
  );
}

export async function fetchParticipantOI(to, from) {
  return apiCall(
    () => stockApi.get('/v1/get_fno_oi', { params: { to, from } }),
    'fetchParticipantOI',
  );
}

export async function fetchStockAtmStraddle(date) {
  return apiCall(
    () => stockApi.get('/v4/stock_atm_straddle', { params: { date } }),
    'fetchStockAtmStraddle',
  );
}

export async function fetchMwplBanList(date) {
  return apiCall(
    () => stockApi.get('/v1/fno-mwpl', { params: { ban: true, date, entrants: true, exits: true } }),
    'fetchMwplBanList',
  );
}

export async function fetchStrategyBars({ tokens, type, from, to }) {
  return apiCall(
    () =>
      stockApi.post(`/v1/strategy-ch?type=${type}&from=${from}&to=${to}`, tokens),
    'fetchStrategyBars',
  );
}
